import { z } from 'zod';
import { compositionSchema } from './assistant';
import { buyerSalesContextSchema } from './sales-psychology';
import { salesTurnUnderstandingSchema } from './sales-turn';
import { uuidV7Schema } from './ids';
import { turnProgressSchema } from './turn-progress';

export const replyCheckSchema = z
  .object({
    key: z.string().regex(/^[a-f0-9]{64}$/u),
    permitId: z.number().int().positive().optional(),
    answers: z.record(
      z.string(),
      z.discriminatedUnion('type', [
        z
          .object({
            type: z.literal('choice'),
            choice: z.string(),
            probabilities: z.record(z.string(), z.number().min(0).max(1)),
            confidence: z.number().min(0).max(1),
          })
          .strict(),
        z
          .object({ type: z.literal('noul'), noul: z.number().min(0).max(1) })
          .strict(),
      ])
    ),
  })
  .strict();
export type ReplyCheck = z.infer<typeof replyCheckSchema>;

export const turnReviewSchema = z
  .object({
    checks: z.array(replyCheckSchema).max(24).optional(),
    inputDigest: z
      .string()
      .regex(/^[a-f0-9]{64}$/u)
      .optional(),
    communication: z
      .object({
        confidence: z.number().min(0).max(1).optional(),
        appropriateProbability: z.number().min(0).max(1).optional(),
        appropriate: z.boolean(),
        salesUseful: z.boolean(),
        claimScope: z.enum(['none', 'business']).optional(),
        answerComplete: z.boolean().optional(),
        salesArgumentFulfilled: z.boolean().optional(),
      })
      .strict()
      .optional(),
    claims: z
      .object({
        supported: z.boolean(),
        unsupportedClaims: z.array(z.string()).max(30),
      })
      .strict()
      .optional(),
  })
  .strict();
export type TurnReview = z.infer<typeof turnReviewSchema>;

export const turnWorkSchema = z
  .object({
    version: z.literal(1),
    releaseId: uuidV7Schema,
    startedAt: z.iso.datetime(),
    activeMs: z.number().nonnegative().optional(),
    claimDecisions: z.record(z.string(), z.boolean()).optional(),
    classifierResults: z.array(replyCheckSchema).optional(),
    completedModelCallIds: z.array(uuidV7Schema).optional(),
    lunaCalls: z.number().int().nonnegative(),
    lunaCorrectionCalls: z.number().int().nonnegative().optional(),
    jevCalls: z.number().int().nonnegative(),
    revisions: z.number().int().min(0).max(1),
    progress: turnProgressSchema.optional(),
    buyer: buyerSalesContextSchema.optional(),
    understanding: salesTurnUnderstandingSchema.optional(),
    candidate: compositionSchema.optional(),
    review: turnReviewSchema.optional(),
  })
  .strict();
export type TurnWork = z.infer<typeof turnWorkSchema>;
